/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Hide-on-scroll-down / reveal-on-scroll-up for the site's chrome: the header
 * below `lg` (and, through `--header-offset`, everything pinned under it —
 * the comparer's sticky filter bar, the progress bar, the floating pager).
 *
 * A pure state machine fed scroll positions, so the rule is pinned in a unit
 * test without a browser (src/lib/__tests__/scrollChrome.test.ts).
 *
 * Why not "delta > 4 px per event", which is what Header.tsx did until
 * 2026-10-01: on a phone a scroll is a stream of small deltas in BOTH
 * directions — the finger settling after a fling, the URL bar resizing the
 * viewport, a momentum tail — and every reversal over 4 px flipped the header.
 * Each flip also moved the sticky filter bar by the header's 80 px, so the top
 * of the screen "breathed" while the visitor was reading results.
 *
 * The pattern every native scroll-away app bar uses instead: count the
 * distance travelled SINCE THE LAST CHANGE OF DIRECTION and act only once it
 * exceeds a per-direction threshold. A reversal smaller than the threshold is
 * noise; the moment the original direction resumes, it is forgotten. Reveal is
 * eager (a visitor scrolling up is reaching for the chrome), hide is lazier (a
 * visitor who only nudges the page must not lose the nav).
 */

export interface ScrollChromeOptions {
  /** Above this scroll position the chrome is always shown: the page top is
   *  on screen, hiding the header there would only leave a hole. */
  topOffset: number;
  /** Continuous downward travel, in px, before the chrome hides. */
  hideAfter: number;
  /** Continuous upward travel, in px, before the chrome reveals. */
  revealAfter: number;
}

/**
 * 48 px down: more than a settling finger or a URL-bar resize ever produces,
 * less than a quarter turn of a thumb. 16 px up: one deliberate nudge. 80 px:
 * the header's own height (h-20).
 */
export const SCROLL_CHROME_DEFAULTS: ScrollChromeOptions = {
  topOffset: 80,
  hideAfter: 48,
  revealAfter: 16,
};

export interface ScrollChromeTracker {
  /** The chrome is out of the way right now. */
  readonly hidden: boolean;
  /** Forget any travel and anchor at `y`: on mount, after a scroll this site
   *  started itself, when a drawer that pinned the chrome closes. */
  reset(y: number): void;
  /** Back in view, any travel forgotten, anchored at `y`: the router opened a
   *  new page at its top (lib/pageScroll.ts `scrollToNewPageTop`), where the
   *  chrome is always shown. Unlike `reset`, this changes the hidden state, so
   *  the next downward stretch is measured from a shown chrome. */
  show(y: number): void;
  /**
   * Feed one scroll position. `maxY` is the furthest the document can scroll
   * (scrollHeight − innerHeight). Returns the new hidden state when it
   * changed on this event, null when nothing changed — so a caller can set
   * React state only on a real transition.
   */
  update(y: number, maxY: number): boolean | null;
}

export const createScrollChromeTracker = (
  options: ScrollChromeOptions = SCROLL_CHROME_DEFAULTS
): ScrollChromeTracker => {
  const { topOffset, hideAfter, revealAfter } = options;
  let lastY = 0;
  /** Signed distance travelled in the current direction (down > 0). */
  let travel = 0;
  let hidden = false;

  const setHidden = (next: boolean): boolean | null => {
    if (next === hidden) return null;
    hidden = next;
    return next;
  };

  return {
    get hidden() {
      return hidden;
    },
    reset(y) {
      lastY = y;
      travel = 0;
    },
    show(y) {
      lastY = y;
      travel = 0;
      hidden = false;
    },
    update(y, maxY) {
      // Rubber-banding past either end of the document (iOS) is not a change
      // of reading direction: ignore it, but re-anchor at the edge so the
      // first in-range event afterwards is measured from there.
      if (y < 0 || y > maxY) {
        lastY = Math.min(Math.max(y, 0), Math.max(maxY, 0));
        travel = 0;
        return null;
      }
      const delta = y - lastY;
      lastY = y;
      if (y < topOffset) {
        travel = 0;
        return setHidden(false);
      }
      if (delta === 0) return null;
      // Same direction as the travel so far: accumulate. A reversal restarts
      // the count from this event's delta alone.
      const sameDirection = travel !== 0 && delta > 0 === travel > 0;
      travel = sameDirection ? travel + delta : delta;
      if (travel >= hideAfter) return setHidden(true);
      if (travel <= -revealAfter) return setHidden(false);
      return null;
    },
  };
};
